import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { Amenity, DraggableGroup } from "rp-components"
import { StoreUtils } from "@/utils/StoreUtils"
import services from "@/services/Services"
import { AmenityBooking } from "@/services/AmenityBookingService"
import { AmenityUnitAvailability, EmergencyContact } from "@/models/BookingEvent"
import _ from "lodash"

export const namespaced: boolean = true

export interface CalendarDateRange {
    since: number
    until: number
}

export interface AmenitiesState {
    amenities?: Amenity[]
    amenityListItems?: DraggableGroup<Amenity>[]
    amenity?: Amenity
    amenityBookings: AmenityBooking[]
    myAmenityBookings: AmenityBooking[]
    calendarDateRange: CalendarDateRange
    availableUnits: AmenityUnitAvailability[]
    selectedAmenityUnitId: string
    loadingUnits: boolean
}

export const state: AmenitiesState = {
    amenities: undefined,
    amenityListItems: undefined,
    amenity: undefined,
    amenityBookings: [],
    myAmenityBookings: [],
    calendarDateRange: {
        since: new Date(new Date().getFullYear(), new Date().getMonth(), 1).valueOf(),
        until: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).valueOf()
    },
    availableUnits: [],
    selectedAmenityUnitId: "",
    loadingUnits: false
}

//===================================
// Getters
//===================================
export const getters: GetterTree<AmenitiesState, RootState> = {}

//===================================
// Actions
//===================================
export const actions: ActionTree<AmenitiesState, RootState> = {
    ...make.actions(state),

    toggleAmenityListItemExpanded({ commit }, index: number) {
        commit("toggleAmenityListItemExpanded", index)
    },

    buildAmenityListItems({ dispatch }) {
        const items = _.chain(state.amenities)
            .cloneDeep()
            .sortBy(["typePriority", "priority"])
            .groupBy("type")
            .map((v, k) => {
                return { title: k, children: v, expanded: true }
            })
            .value()

        dispatch("setAmenityListItems", items)
    },

    /**
     * Loads all amenities for a property and builds the list items.
     */
    async loadPropertyAmenities({ dispatch }, propertyId: number) {
        await StoreUtils.withLoading(dispatch, async () => {
            const amenities = await services.amenities.listBookableAmenities(propertyId)
            dispatch("setAmenities", amenities)
            dispatch("buildAmenityListItems")
        })
    },

    /**
     * Loads a single amenity by ID.
     */
    async loadAmenity({ dispatch }, payload: { propertyId: number; amenityId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const amenity = await services.amenities.getAmenity(payload.propertyId, payload.amenityId)
            dispatch("setAmenity", amenity)
        })
    },

    /**
     * Loads all bookings for an amenity on a given calendar date.
     */
    async loadAmenityBookings({ dispatch }, payload: { propertyId: number; amenityId: string; date: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const response = await services.amenityBookings.getAmenityBookings(payload.propertyId, payload.amenityId, payload.date)
            dispatch("setAmenityBookings", response.bookings)
        })
    },

    /**
     * Loads the current user's upcoming amenity bookings.
     */
    async loadUserBookings({ dispatch }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const bookings = await services.amenityBookings.getUserUpcomingBookings()
            dispatch("setMyAmenityBookings", bookings)
        })
    },

    /**
     * Loads availability of amenity units for a given time window.
     * Auto-selects the first available unit.
     */
    async loadAmenityUnitAvailability(
        { dispatch },
        payload: { propertyId: number; amenityId: string; since: number; until: number; excludeBookingId?: string }
    ) {
        dispatch("setLoadingUnits", true)
        try {
            const units = await services.amenityBookings.getAmenityUnitAvailability(
                payload.propertyId,
                payload.amenityId,
                payload.since,
                payload.until,
                payload.excludeBookingId
            )
            const sorted = [...units].sort((a, b) => a.amenityUnitName.localeCompare(b.amenityUnitName))
            dispatch("setAvailableUnits", sorted)
            const first = sorted.find((u) => u.available)
            dispatch("setSelectedAmenityUnitId", first?.amenityUnitId || "")
        } finally {
            dispatch("setLoadingUnits", false)
        }
    },

    /**
     * Creates a new amenity booking order.
     */
    async createBookingOrder(
        { dispatch, state: localState },
        payload: {
            propertyId: number
            amenityId: string
            amenityUnitId: string
            userEmail: string
            startTime: number
            endTime: number
            guests?: Array<{ bookingId?: string; email: string; firstName?: string; lastName?: string }>
            emergencyContact?: EmergencyContact
        }
    ) {
        dispatch("setLoading", true, { root: true })
        try {
            const booking = await services.amenityBookings.createBooking({
                propertyId: payload.propertyId,
                amenityId: payload.amenityId,
                amenityUnitId: payload.amenityUnitId,
                amenityName: (localState as AmenitiesState).amenity?.title,
                userEmail: payload.userEmail,
                startTime: payload.startTime,
                endTime: payload.endTime,
                status: "confirmed",
                guests: payload.guests,
                emergencyContact: payload.emergencyContact
            })
            return booking
        } finally {
            dispatch("setLoading", false, { root: true })
            await dispatch("loadUserBookings")
        }
    },

    /**
     * Updates an existing amenity booking order.
     */
    async updateBookingOrder(
        { dispatch, state: localState },
        payload: {
            bookingId: string
            propertyId: number
            amenityId: string
            amenityUnitId: string
            userEmail: string
            startTime: number
            endTime: number
            guests?: Array<{ bookingId?: string; email: string; firstName?: string; lastName?: string }>
            emergencyContact?: EmergencyContact
        }
    ) {
        dispatch("setLoading", true, { root: true })
        try {
            const booking = await services.amenityBookings.updateBooking(payload.propertyId, payload.bookingId, {
                propertyId: payload.propertyId,
                amenityId: payload.amenityId,
                amenityUnitId: payload.amenityUnitId,
                amenityName: (localState as AmenitiesState).amenity?.title,
                userEmail: payload.userEmail,
                startTime: payload.startTime,
                endTime: payload.endTime,
                guests: payload.guests,
                emergencyContact: payload.emergencyContact
            })
            return booking
        } finally {
            dispatch("setLoading", false, { root: true })
            await dispatch("loadUserBookings")
        }
    }
}

//===================================
// Mutations
//===================================
export const mutations: MutationTree<AmenitiesState> = {
    ...make.mutations(state),

    toggleAmenityListItemExpanded(state, index) {
        if (state.amenityListItems) {
            state.amenityListItems[index].expanded = !state.amenityListItems[index].expanded
        }
    }
}

//===================================
// Module
//===================================
export const amenities: Module<AmenitiesState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
